// Added by Sejune Oh on 2019-03-28
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { action, makeObservable, observable } from 'mobx'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Root Store
import RootStore from '../index'

// Store
import MgmtStoreBase from '../base/MgmtStoreBase'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
interface DicomPeerElementBaseProps {
  id?: string
  description?: string
  ae_title?: string
  ip_address?: string
  port?: string
  fk_group_id?: string
  role?: string
  created_at?: string
  updated_at?: string
  deleted_at?: string
}

export interface DicomPeerElementProps extends DicomPeerElementBaseProps {
  roles?: Array<string>
  group_key?: string
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default class DicomPeerStore extends MgmtStoreBase<DicomPeerElementProps> {
  elements: Array<DicomPeerElementProps> = []
  totalCount: number = 0
  root?: RootStore

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  constructor(root: RootStore) {
    super({ storeName: 'DicomPeerStore', apiUrl: 'mgmt/dicompeers', _root: root })
    this.root = root
    makeObservable(this, {
      elements: observable,
      totalCount: observable,
      _setTotalCount: action,
      _setElements: action,
      setElementsWithGroupName: action,
    })
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  getElement = async (id: string) => {
    return await super.getSingle(id)
  }

  getElements = async (path: string) => {
    const rtnData = await super.getAll(path)
    await this._setTotalCount(parseInt(rtnData.total_count as string))
    return rtnData.items as Array<DicomPeerElementProps>
  }

  addElement = async (element: DicomPeerElementProps) => {
    const newElement = await super.add(element)
    return newElement.id
  }

  removeElement = async (id: string) => {
    return await super.remove(id)
  }

  updateElement = async (id: string, element: DicomPeerElementProps) => {
    const updatedElement = await super.updateSingle(id, element)
    return updatedElement.id
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  getAvailableRoles = async () => {
    const { roles } = await super.getSingleWithoutId('/supports/roles')
    return roles
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  checkDuplicatedAETitle = (aeTitle: string) => {
    return super.checkDuplicatedByField({ field: 'ae_title', value: aeTitle })
  }

  checkDuplicatedAETitleWithGroup = (aeTitle: string, groupId: string) => {
    return super.checkDuplicatedByMultiField({
      fields: ['ae_title', 'fk_group_id'],
      values: [aeTitle, groupId],
    })
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _setTotalCount = (count: number) => {
    this.totalCount = count
  }

  _setElements = (elements: Array<DicomPeerElementProps>) => {
    this.elements = elements
  }

  setElementsWithGroupName = (elements: Array<DicomPeerElementProps>, groupElements: Map<string, string>) => {
    this.elements = elements.map((element) => {
      return {
        ...element,
        port: !element.port || element.port! === '0' ? '-' : element.port,
        group_key: groupElements.get(element.fk_group_id!),
      }
    })
  }
}
